import { DateTime } from "luxon";
import {
  IAM_CONTRACT,
  TWU_CONTRACT,
  MIN_TOR_SHIFT_LENGTH_1_HOUR,
  HAT_HOURS_LENGTH_MINUTES_MAXIMUM,
  _4HOUR_VAC_LENGTH_MINUTES,
  MAX_IAM_SHIFT_LENGTH_MINUTES,
  HAT_INCREMENT_LENGTH_MINUTES,
  MAX_TWU_SHIFT_LENGTH_MINUTES,
  DAT_TIME_OFF_TYPE,
  FTO_TIME_OFF_TYPE,
  FTO_GOC_HOL_TIME_OFF_TYPE,
  EAD_TIME_OFF_TYPE,
  EDU_TIME_OFF_TYPE,
  _4HOUR_VAC_TIME_OFF_TYPE,
  HAT_TIME_OFF_TYPE,
  PTO_TIME_OFF_TYPE,
  HOL_TIME_OFF_TYPE,
  SEAD_TIME_OFF_TYPE,
  NON_CONTRACT,
  JUNE_MONTH_NUM,
  FEBRUARY_MONTH_NUM,
  DECEMBER_MONTH_NUM,
  HAT_HOURS_LENGTH_MINUTES_MINIMUM,
  MIN_TOR_SHIFT_LENGTH_4_HOURS,
  LWOP_ALL_DAY_TIME_OFF_TYPE,
  TOWOP_ALL_DAY_TIME_OFF_TYPE,
  TOWOP_DAY_OF_TIME_OFF_TYPE,
  LWOP_DAY_OF_TIME_OFF_TYPE,
} from "./constants";
import {
  THREE_MONTHS_IN_FUTURE,
  TODAY,
  TOMORROW,
  NEXT_LAST_DAY_OF_MONTH,
  TWO_MONTHS_IN_PAST,
  getTimeDifferenceInMinutes,
  FIFTEEN_DAYS_IN_FUTURE,
} from "./dateHelper";
import { setStartDateTime, setEndDateTime } from "./inputValidation";
import {
  HAT_SHIFT_1_HOUR_INCREMENT_WARNING_MESSAGE,
  HAT_SHIFT_MIN_MAX_WARNING_MESSAGE,
  LWOP_TOWOP_ALL_DAY_DEADLINE_MESSAGE,
  MAX_8_AND_ONE_HALF_HOURS_WARNING_MESSAGE,
  MAX_SHIFT_10_AND_ONE_HALF_HOURS_WARNING_MESSAGE,
  MINIMUM_SHIFT_LENGTH_WARNING__1_HOUR,
  MINIMUM_SHIFT_LENGTH_WARNING__4_HOURS,
  WOP_RELEASE_MIN_TIME_WARNING_MESSAGE,
  WOP_RELEASE_WITHIN_SHIFT_TIME_WARNING_MESSAGE,
  WOP_START_END_TIME_IN_PAST_WARNING_MESSAGE,
  _4_HOUR_VAC_SHIFT_WARNING_MESSAGE,
} from "./messageConstants";
import { set_time_to_end_of_day, set_time_to_midnight } from "./dateTimeFormatter";
import { getTimeZoneFromSwaStation } from "./timeZoneService";

const DAT_FTO_CONTENT = {
  [IAM_CONTRACT]: {
    header: "IAM REMINDER:",
    cancelHeader: "IAM CUTOFF TIME REMINDER:",
    message: "DAT / FTO requests within one hour of the shift start time must be submitted verbally to management.",
    cancelMessage:
      "Requests to cancel your DAT / FTO with less than 72 hours notice are subject to management approval.",
  },
  [TWU_CONTRACT]: {
    header: "TWU CUTOFF TIME REMINDER:",
    cancelHeader: "TWU CUTOFF TIME REMINDER:",
    message:
      "DAT / FTO requests submitted less than 24 hours prior to time requested are subject to management approval.",
    cancelMessage: "Requests to cancel your DAT/FTO with less than 72 hours notice are subject to management approval.",
  },
};

const DAT_FTO_HOLD_REQUEST_CONTENT = {
  [IAM_CONTRACT]: {
    header: "IAM HOLD REQUEST REMINDER:",
    cancelHeader: "IAM HOLD REQUEST REMINDER:",
    message:
      "If the requested day is fully booked and you have not received a response, your request will be held until we determine whether voluntary overtime coverage can be arranged after the overtime book is closed.",
  },
  [TWU_CONTRACT]: {
    header: "TWU HOLD REQUEST REMINDER:",
    cancelHeader: "TWU HOLD REQUEST REMINDER:",
    message:
      "If the requested day is fully booked and you have not received a response, your request will be held until we determine whether voluntary overtime coverage can be arranged after the overtime book is closed.",
  },
};

const PTO_NON_CONTRACT_CONTENT = {
  [NON_CONTRACT]: {
    header: "CUTOFF TIME REMINDER:",
    cancelHeader: "CUTOFF TIME REMINDER:",
    message: "PTO requests are subject to management approval.",
    cancelMessage:
      "Requests to cancel your PTO after 12:00 noon on the day prior to date requested off are subject to management approval.",
  },
};
const HOL_NON_CONTRACT_CONTENT = {
  [NON_CONTRACT]: {
    header: "CUTOFF TIME REMINDER:",
    cancelHeader: "CUTOFF TIME REMINDER:",
    message:
      "HOL requests submitted after 17:00 on the day prior to the date requested off are subject to management approval.",
    cancelMessage:
      "Requests to cancel your HOL after 12:00 noon on the day prior to date requested off are subject to management approval.",
  },
};

const FTO_GOC_HOL_NON_CONTRACT_CONTENT = {
  [NON_CONTRACT]: {
    header: "CUTOFF TIME REMINDER:",
    cancelHeader: "CUTOFF TIME REMINDER:",
    message:
      "FTO/GOC-HOL requests submitted after 17:00 on the day prior to the date requested off are subject to management approval.",
    cancelMessage:
      "Requests to cancel your FTO/GOC-HOL after 12:00 noon on the day prior to date requested off are subject to management approval.",
  },
};

const SEAD_NON_CONTRACT_CONTENT = {
  [NON_CONTRACT]: {
    header: "CUTOFF TIME REMINDER:",
    cancelHeader: "CUTOFF TIME REMINDER:",
    message:
      "SEAD requests submitted after 17:00 on the day prior to the date requested off are subject to management approval.",
    cancelMessage:
      "Requests to cancel your SEAD after 12:00 noon on the day prior to date requested off are subject to management approval.",
  },
};

const EAD_EDU_CONTENT = {
  [IAM_CONTRACT]: {
    header: "IAM CUTOFF TIME REMINDER:",
    cancelHeader: "IAM CUTOFF TIME REMINDER:",
    message: "EAD / EDU requests submitted less than 24 hours in advance are subject to management approval.",
  },
  [TWU_CONTRACT]: {
    header: "TWU CUTOFF TIME REMINDER:",
    cancelHeader: "TWU CUTOFF TIME REMINDER:",
    message: "EAD requests submitted less than 24 hours in advance are subject to management approval.",
    cancelMessage: "Requests to cancel your EAD with less than 72 hours notice is subject to management approval.",
  },
};

const _4HOUR_VAC_HAT_CONTENT = {
  [IAM_CONTRACT]: {
    header: "IAM CUTOFF TIME REMINDER:",
    cancelHeader: "IAM CUTOFF TIME REMINDER:",
    message: "4-Hour VAC / HAT requests are subject to management approval.",
    cancelMessage: "Requests to cancel your 4-Hour VAC / HAT are subject to management approval.",
  },
  // TWU_CONTRACT does not have 4-Hour VAC/HAT
};

const WOP_ALL_DAY_CONTENT = {
  [NON_CONTRACT]: {
    header: "CUTOFF TIME REMINDER:",
    message: LWOP_TOWOP_ALL_DAY_DEADLINE_MESSAGE,
  },
  [IAM_CONTRACT]: {
    header: "IAM REMINDER:",
    message: LWOP_TOWOP_ALL_DAY_DEADLINE_MESSAGE,
  },
  [TWU_CONTRACT]: {
    header: "TWU CUTOFF TIME REMINDER:",
    message: LWOP_TOWOP_ALL_DAY_DEADLINE_MESSAGE,
  },
};

const getPastTimePeriod = (station, isAdminPage = null) => {
  let pastTime = TODAY(station);
  if (isAdminPage) {
    return TWO_MONTHS_IN_PAST(station);
  }
  return pastTime;
};

export const TOR_SUBMISSION_CUTOFF_TIME_REMINDER = {
  [IAM_CONTRACT]: {
    [DAT_TIME_OFF_TYPE]: {
      warningContent: DAT_FTO_CONTENT[IAM_CONTRACT],
      holdingRequestWarningContent: DAT_FTO_HOLD_REQUEST_CONTENT[IAM_CONTRACT],
      getMaxSubmissionShiftDate: (station) => THREE_MONTHS_IN_FUTURE(station),
      getMinSubmissionShiftDate: (station, isAdminPage) => getPastTimePeriod(station, isAdminPage),
    },
    [FTO_TIME_OFF_TYPE]: {
      warningContent: DAT_FTO_CONTENT[IAM_CONTRACT],
      holdingRequestWarningContent: DAT_FTO_HOLD_REQUEST_CONTENT[IAM_CONTRACT],
      getMaxSubmissionShiftDate: (station) => THREE_MONTHS_IN_FUTURE(station),
      getMinSubmissionShiftDate: (station, isAdminPage) => getPastTimePeriod(station, isAdminPage),
    },
    [EAD_TIME_OFF_TYPE]: {
      warningContent: EAD_EDU_CONTENT[IAM_CONTRACT],
      getMaxSubmissionShiftDate: (station) => NEXT_LAST_DAY_OF_MONTH(station, JUNE_MONTH_NUM),
      getMinSubmissionShiftDate: (station, isAdminPage) => getPastTimePeriod(station, isAdminPage),
    },
    [EDU_TIME_OFF_TYPE]: {
      warningContent: EAD_EDU_CONTENT[IAM_CONTRACT],
      getMaxSubmissionShiftDate: (station) => NEXT_LAST_DAY_OF_MONTH(station, JUNE_MONTH_NUM),
      getMinSubmissionShiftDate: (station, isAdminPage) => getPastTimePeriod(station, isAdminPage),
    },
    [_4HOUR_VAC_TIME_OFF_TYPE]: {
      warningContent: _4HOUR_VAC_HAT_CONTENT[IAM_CONTRACT],
      getMaxSubmissionShiftDate: (station) => THREE_MONTHS_IN_FUTURE(station),
      getMinSubmissionShiftDate: (station, isAdminPage) => getPastTimePeriod(station, isAdminPage),
    },
    [HAT_TIME_OFF_TYPE]: {
      warningContent: _4HOUR_VAC_HAT_CONTENT[IAM_CONTRACT],
      getMaxSubmissionShiftDate: (station) => TOMORROW(station),
      getMinSubmissionShiftDate: (station, isAdminPage) => getPastTimePeriod(station, isAdminPage),
    },
    [PTO_TIME_OFF_TYPE]: {
      warningContent: DAT_FTO_CONTENT[IAM_CONTRACT],
      getMaxSubmissionShiftDate: (station) => THREE_MONTHS_IN_FUTURE(station),
      getMinSubmissionShiftDate: (station, isAdminPage) => getPastTimePeriod(station, isAdminPage),
    },
    [HOL_TIME_OFF_TYPE]: {
      warningContent: DAT_FTO_CONTENT[IAM_CONTRACT],
      getMaxSubmissionShiftDate: (station) => THREE_MONTHS_IN_FUTURE(station),
      getMinSubmissionShiftDate: (station, isAdminPage) => getPastTimePeriod(station, isAdminPage),
    },
    [SEAD_TIME_OFF_TYPE]: {
      warningContent: DAT_FTO_CONTENT[IAM_CONTRACT],
      getMaxSubmissionShiftDate: (station) => THREE_MONTHS_IN_FUTURE(station),
      getMinSubmissionShiftDate: (station, isAdminPage) => getPastTimePeriod(station, isAdminPage),
    },
    [TOWOP_ALL_DAY_TIME_OFF_TYPE]: {
      warningContent: WOP_ALL_DAY_CONTENT[IAM_CONTRACT],
      getMaxSubmissionShiftDate: (station) => FIFTEEN_DAYS_IN_FUTURE(station),
      getMinSubmissionShiftDate: (station) => TOMORROW(station),
    },
    [TOWOP_DAY_OF_TIME_OFF_TYPE]: {
      getMaxSubmissionShiftDate: (station) => TODAY(station),
      getMinSubmissionShiftDate: (station) => TODAY(station),
    },
  },
  [TWU_CONTRACT]: {
    [DAT_TIME_OFF_TYPE]: {
      warningContent: DAT_FTO_CONTENT[TWU_CONTRACT],
      holdingRequestWarningContent: DAT_FTO_HOLD_REQUEST_CONTENT[TWU_CONTRACT],
      getMaxSubmissionShiftDate: (station) => THREE_MONTHS_IN_FUTURE(station),
      getMinSubmissionShiftDate: (station, isAdminPage) => getPastTimePeriod(station, isAdminPage),
    },
    [FTO_TIME_OFF_TYPE]: {
      warningContent: DAT_FTO_CONTENT[TWU_CONTRACT],
      holdingRequestWarningContent: DAT_FTO_HOLD_REQUEST_CONTENT[TWU_CONTRACT],
      getMaxSubmissionShiftDate: (station) => THREE_MONTHS_IN_FUTURE(station),
      getMinSubmissionShiftDate: (station, isAdminPage) => getPastTimePeriod(station, isAdminPage),
    },
    [EAD_TIME_OFF_TYPE]: {
      warningContent: EAD_EDU_CONTENT[TWU_CONTRACT],
      getMaxSubmissionShiftDate: (station) => NEXT_LAST_DAY_OF_MONTH(station, FEBRUARY_MONTH_NUM),
      getMinSubmissionShiftDate: (station, isAdminPage) => getPastTimePeriod(station, isAdminPage),
    },
    [LWOP_ALL_DAY_TIME_OFF_TYPE]: {
      warningContent: WOP_ALL_DAY_CONTENT[TWU_CONTRACT],
      getMaxSubmissionShiftDate: (station) => FIFTEEN_DAYS_IN_FUTURE(station),
      getMinSubmissionShiftDate: (station) => TOMORROW(station),
    },
    [LWOP_DAY_OF_TIME_OFF_TYPE]: {
      getMaxSubmissionShiftDate: (station) => TODAY(station),
      getMinSubmissionShiftDate: (station) => TODAY(station),
    },
  },
  [NON_CONTRACT]: {
    [PTO_TIME_OFF_TYPE]: {
      warningContent: PTO_NON_CONTRACT_CONTENT[NON_CONTRACT],
      getMaxSubmissionShiftDate: (station) => THREE_MONTHS_IN_FUTURE(station),
      getMinSubmissionShiftDate: (station, isAdminPage) => getPastTimePeriod(station, isAdminPage),
    },
    [HOL_TIME_OFF_TYPE]: {
      warningContent: HOL_NON_CONTRACT_CONTENT[NON_CONTRACT],
      getMaxSubmissionShiftDate: (station) => THREE_MONTHS_IN_FUTURE(station),
      getMinSubmissionShiftDate: (station, isAdminPage) => getPastTimePeriod(station, isAdminPage),
    },
    [FTO_GOC_HOL_TIME_OFF_TYPE]: {
      warningContent: FTO_GOC_HOL_NON_CONTRACT_CONTENT[NON_CONTRACT],
      getMaxSubmissionShiftDate: (station) => THREE_MONTHS_IN_FUTURE(station),
      getMinSubmissionShiftDate: (station, isAdminPage) => getPastTimePeriod(station, isAdminPage),
    },
    [SEAD_TIME_OFF_TYPE]: {
      warningContent: SEAD_NON_CONTRACT_CONTENT[NON_CONTRACT],
      getMaxSubmissionShiftDate: (station) => NEXT_LAST_DAY_OF_MONTH(station, DECEMBER_MONTH_NUM),
      getMinSubmissionShiftDate: (station, isAdminPage) => getPastTimePeriod(station, isAdminPage),
    },
    [LWOP_ALL_DAY_TIME_OFF_TYPE]: {
      warningContent: WOP_ALL_DAY_CONTENT[NON_CONTRACT],
      getMaxSubmissionShiftDate: (station) => FIFTEEN_DAYS_IN_FUTURE(station),
      getMinSubmissionShiftDate: (station) => TOMORROW(station),
    },
    [LWOP_DAY_OF_TIME_OFF_TYPE]: {
      getMaxSubmissionShiftDate: (station) => TODAY(station),
      getMinSubmissionShiftDate: (station) => TODAY(station),
    },
  },
};

export const TOR_SUBMISSION_HOLD_REQUEST_REMINDER = {
  [IAM_CONTRACT]: {
    [DAT_TIME_OFF_TYPE]: {
      warningContent: DAT_FTO_HOLD_REQUEST_CONTENT[IAM_CONTRACT],
    },
    [FTO_TIME_OFF_TYPE]: {
      warningContent: DAT_FTO_HOLD_REQUEST_CONTENT[IAM_CONTRACT],
    },
  },
  [TWU_CONTRACT]: {
    [DAT_TIME_OFF_TYPE]: {
      warningContent: DAT_FTO_HOLD_REQUEST_CONTENT[TWU_CONTRACT],
    },
    [FTO_TIME_OFF_TYPE]: {
      warningContent: DAT_FTO_HOLD_REQUEST_CONTENT[TWU_CONTRACT],
    },
  },
};

export const timeOffRequestDateTimeValidation = (
  startDate,
  endDate,
  startHour,
  startMin,
  endHour,
  endMin,
  station,
  timeOffType,
  contractType
) => {
  if (contractType === IAM_CONTRACT) {
    if ((!!startDate, !!endDate, startHour !== "", startMin !== "", endHour !== "", endMin !== "")) {
      return timeOffRequestDateTimeValidationIAM(
        startDate,
        endDate,
        startHour,
        startMin,
        endHour,
        endMin,
        station,
        timeOffType
      );
    } else {
      return "";
    }
  } else if (contractType === TWU_CONTRACT) {
    if ((!!startDate, !!endDate, startHour !== "", startMin !== "", endHour !== "", endMin !== "")) {
      return timeOffRequestDateTimeValidationTWU(
        startDate,
        endDate,
        startHour,
        startMin,
        endHour,
        endMin,
        station,
        timeOffType
      );
    }
  } else {
    return timeOffRequestDateTimeValidationNONCONTRACT(
      startDate,
      endDate,
      startHour,
      startMin,
      endHour,
      endMin,
      station,
      timeOffType
    );
  }
};

export const timeOffRequestDateTimeValidationIAM = (
  startDate,
  endDate,
  startHour,
  startMin,
  endHour,
  endMin,
  station,
  timeOffType
) => {
  let startDateTimeVal = setStartDateTime(startDate, startHour, startMin, station);
  let endDateTimeVal = setEndDateTime(endDate, endHour, endMin, station);
  let timeDifference = getTimeDifferenceInMinutes(startDateTimeVal, endDateTimeVal);

  if (timeOffType === _4HOUR_VAC_TIME_OFF_TYPE) {
    if (timeDifference !== _4HOUR_VAC_LENGTH_MINUTES) {
      return _4_HOUR_VAC_SHIFT_WARNING_MESSAGE;
    }
  } else if (timeOffType === HAT_TIME_OFF_TYPE) {
    if (timeDifference > HAT_HOURS_LENGTH_MINUTES_MAXIMUM || timeDifference < HAT_HOURS_LENGTH_MINUTES_MINIMUM) {
      return HAT_SHIFT_MIN_MAX_WARNING_MESSAGE;
    }
    if (timeDifference % HAT_INCREMENT_LENGTH_MINUTES !== 0) {
      return HAT_SHIFT_1_HOUR_INCREMENT_WARNING_MESSAGE;
    }
  } else if (isEADorEDUorSEAD(timeOffType) && startDateTimeVal.equals(endDateTimeVal)) {
    return "";
  } else {
    if (
      getMinimumShiftLength(timeOffType) === MIN_TOR_SHIFT_LENGTH_1_HOUR &&
      timeDifference < MIN_TOR_SHIFT_LENGTH_1_HOUR
    ) {
      return MINIMUM_SHIFT_LENGTH_WARNING__1_HOUR;
    }
    if (
      getMinimumShiftLength(timeOffType) === MIN_TOR_SHIFT_LENGTH_4_HOURS &&
      timeDifference < MIN_TOR_SHIFT_LENGTH_4_HOURS
    ) {
      return MINIMUM_SHIFT_LENGTH_WARNING__4_HOURS;
    }
    if (timeDifference > MAX_IAM_SHIFT_LENGTH_MINUTES) {
      return MAX_SHIFT_10_AND_ONE_HALF_HOURS_WARNING_MESSAGE;
    }
  }
};

export const timeOffRequestDateTimeValidationTWU = (
  startDate,
  endDate,
  startHour,
  startMin,
  endHour,
  endMin,
  station,
  timeOffType
) => {
  let startDateTimeVal = setStartDateTime(startDate, startHour, startMin, station);
  let endDateTimeVal = setEndDateTime(endDate, endHour, endMin, station);
  let timeDifference = getTimeDifferenceInMinutes(startDateTimeVal, endDateTimeVal);

  if (isEADorEDUorSEAD(timeOffType) && startDateTimeVal.equals(endDateTimeVal)) {
    return "";
  } else {
    if (
      getMinimumShiftLength(timeOffType) === MIN_TOR_SHIFT_LENGTH_1_HOUR &&
      timeDifference < MIN_TOR_SHIFT_LENGTH_1_HOUR
    ) {
      return MINIMUM_SHIFT_LENGTH_WARNING__1_HOUR;
    }
    if (
      getMinimumShiftLength(timeOffType) === MIN_TOR_SHIFT_LENGTH_4_HOURS &&
      timeDifference < MIN_TOR_SHIFT_LENGTH_4_HOURS
    ) {
      return MINIMUM_SHIFT_LENGTH_WARNING__4_HOURS;
    }
    if (timeDifference > MAX_TWU_SHIFT_LENGTH_MINUTES) {
      return MAX_8_AND_ONE_HALF_HOURS_WARNING_MESSAGE;
    }
  }
};

export const timeOffRequestDateTimeValidationNONCONTRACT = (
  startDate,
  endDate,
  startHour,
  startMin,
  endHour,
  endMin,
  station,
  timeOffType
) => {
  if (
    startHour.toString().trim() === "" ||
    startMin.toString().trim() === "" ||
    endHour.toString().trim() === "" ||
    endMin.toString().trim() === ""
  ) {
    return "";
  }
  const startDateTimeVal = setStartDateTime(startDate, startHour, startMin, station);
  const endDateTimeVal = setEndDateTime(endDate, endHour, endMin, station);
  let timeDifference = getTimeDifferenceInMinutes(startDateTimeVal, endDateTimeVal);

  if (isEADorEDUorSEAD(timeOffType) && startDateTimeVal.equals(endDateTimeVal)) {
    return "";
  } else {
    if (
      getMinimumShiftLength(timeOffType) === MIN_TOR_SHIFT_LENGTH_1_HOUR &&
      timeDifference < MIN_TOR_SHIFT_LENGTH_1_HOUR
    ) {
      return MINIMUM_SHIFT_LENGTH_WARNING__1_HOUR;
    }
    if (
      getMinimumShiftLength(timeOffType) === MIN_TOR_SHIFT_LENGTH_4_HOURS &&
      timeDifference < MIN_TOR_SHIFT_LENGTH_4_HOURS
    ) {
      return MINIMUM_SHIFT_LENGTH_WARNING__4_HOURS;
    }
    if (timeDifference > MAX_TWU_SHIFT_LENGTH_MINUTES) {
      return MAX_8_AND_ONE_HALF_HOURS_WARNING_MESSAGE;
    }
  }
};

export const isEADorEDUorSEAD = (timeOffType) => {
  if (timeOffType === EAD_TIME_OFF_TYPE || timeOffType === EDU_TIME_OFF_TYPE || timeOffType === SEAD_TIME_OFF_TYPE) {
    return true;
  } else {
    return false;
  }
};

export const isLWOPTOWOPRequest = (timeOffType) => {
  return (
    timeOffType === LWOP_ALL_DAY_TIME_OFF_TYPE ||
    timeOffType === LWOP_DAY_OF_TIME_OFF_TYPE ||
    timeOffType === TOWOP_DAY_OF_TIME_OFF_TYPE ||
    timeOffType === TOWOP_ALL_DAY_TIME_OFF_TYPE
  );
};

export const isLWOPTOWOPDayOfRequest = (timeOffType) => {
  return timeOffType === LWOP_DAY_OF_TIME_OFF_TYPE || timeOffType === TOWOP_DAY_OF_TIME_OFF_TYPE;
};

export const isLWOPTOWOPAllDayRequest = (timeOffType) => {
  return timeOffType === TOWOP_ALL_DAY_TIME_OFF_TYPE || timeOffType === LWOP_ALL_DAY_TIME_OFF_TYPE;
};

export const validateWOPDeadline = (
  shiftStart,
  shiftEnd,
  preferredReleaseStart,
  preferredReleaseEnd,
  isASAP,
  station,
  timeOffType
) => {
  const preferredWOPStartHr = preferredReleaseStart.preferredWOPStartHour;
  const preferredWOPStartMin = preferredReleaseStart.preferredWOPStartMin;
  const preferredWOPEndHr = preferredReleaseEnd.preferredWOPEndHour;
  const preferredWOPEndMin = preferredReleaseEnd.preferredWOPEndMin;
  const now = DateTime.now().setZone(getTimeZoneFromSwaStation(station));

  const deadline = isLWOPTOWOPAllDayRequest(timeOffType) ? set_time_to_midnight(shiftStart) : shiftEnd;
  const shiftInPast = deadline.toMillis() < now.toMillis();

  let leaveTimeInPast = false;
  if (!isASAP) {
    leaveTimeInPast = isPreferredLeaveTimeInPast(
      shiftStart,
      shiftEnd,
      station,
      preferredWOPStartHr,
      preferredWOPStartMin,
      preferredWOPEndHr,
      preferredWOPEndMin
    );
  }

  if (shiftInPast || leaveTimeInPast) {
    return "Shift is past the submission deadline!";
  }

  return "";
};

export const validateWOPPreferredLeaveTime = (
  shiftStart,
  shiftEnd,
  preferredReleaseStart,
  preferredReleaseEnd,
  isASAP,
  station,
  isAdminRequest
) => {
  const preferredWOPStartHr = preferredReleaseStart.preferredWOPStartHour;
  const preferredWOPStartMin = preferredReleaseStart.preferredWOPStartMin;
  let preferredWOPEndHr = preferredReleaseEnd.preferredWOPEndHour;
  const preferredWOPEndMin = preferredReleaseEnd.preferredWOPEndMin;

  if (!isWOPRequest15MinsMinimum(preferredWOPStartHr, preferredWOPStartMin, preferredWOPEndHr, preferredWOPEndMin)) {
    return WOP_RELEASE_MIN_TIME_WARNING_MESSAGE;
  }

  if (
    !isWOPRequestWithinShiftTime(
      shiftStart,
      shiftEnd,
      preferredWOPStartHr,
      preferredWOPStartMin,
      preferredWOPEndHr,
      preferredWOPEndMin,
      isASAP
    )
  ) {
    return WOP_RELEASE_WITHIN_SHIFT_TIME_WARNING_MESSAGE;
  }

  // Preferred Leave Times cannot be in the past for employees
  if (
    !isAdminRequest &&
    isPreferredLeaveTimeInPast(
      shiftStart,
      shiftEnd,
      station,
      preferredWOPStartHr,
      preferredWOPStartMin,
      preferredWOPEndHr,
      preferredWOPEndMin
    )
  ) {
    return WOP_START_END_TIME_IN_PAST_WARNING_MESSAGE;
  }

  return "";
};

const isWOPRequest15MinsMinimum = (startHour, startMin, endHour, endMin) => {
  if (endHour < startHour) {
    endHour += 24;
  }

  return endHour > startHour || (startHour === endHour && endMin - startMin >= 15);
};

const isWOPRequestWithinShiftTime = (
  shiftStart,
  shiftEnd,
  wopRequestStartHr,
  wopRequestStartMin,
  wopRequestEndHr,
  wopRequestEndMin,
  isASAP
) => {
  const requestDateTimes = getWOPRequestDateTimes(
    shiftStart,
    shiftEnd,
    wopRequestStartHr,
    wopRequestStartMin,
    wopRequestEndHr,
    wopRequestEndMin,
    isASAP
  );

  return !(requestDateTimes === null);
};

export const isPreferredLeaveTimeInPast = (
  shiftStart,
  shiftEnd,
  station,
  preferredWOPStartHr,
  preferredWOPStartMin,
  preferredWOPEndHr,
  preferredWOPEndMin
) => {
  const wopPreferredLeaveDates = getWOPRequestDateTimes(
    shiftStart,
    shiftEnd,
    preferredWOPStartHr,
    preferredWOPStartMin,
    preferredWOPEndHr,
    preferredWOPEndMin,
    false
  );
  const now = DateTime.now().setZone(getTimeZoneFromSwaStation(station));

  return (
    wopPreferredLeaveDates.requestStart.toMillis() < now.toMillis() ||
    wopPreferredLeaveDates.requestEnd.toMillis() < now.toMillis()
  );
};

export const getWOPRequestDateTimes = (
  shiftStart,
  shiftEnd,
  wopRequestStartHr,
  wopRequestStartMin,
  wopRequestEndHr,
  wopRequestEndMin,
  isASAP
) => {
  let requestStart = null;
  let requestEnd = null;

  if (isASAP) {
    return {
      requestStart: shiftStart.set({ hour: 0, minute: 0 }),
      requestEnd: shiftEnd.set({ hour: 0, minute: 0 }),
    };
  }

  const leaveStartCandidates = [
    shiftStart.set({ hour: wopRequestStartHr, minute: wopRequestStartMin }),
    shiftEnd.set({ hour: wopRequestStartHr, minute: wopRequestStartMin }),
  ];

  const leaveEndCandidates = [
    shiftStart.set({ hour: wopRequestEndHr, minute: wopRequestEndMin }),
    shiftEnd.set({ hour: wopRequestEndHr, minute: wopRequestEndMin }),
  ];

  const requestDates = leaveStartCandidates
    .flatMap((start) => leaveEndCandidates.map((end) => ({ start, end })))
    .filter(({ start, end }) => start >= shiftStart && end <= shiftEnd && end > start);

  if (requestDates.length === 0) {
    return null;
  }

  requestStart = requestDates[0].start;
  requestEnd = requestDates[0].end;

  return { requestStart, requestEnd };
};

export const getWOPDeadline = (timeOffType, shiftStart, shiftEnd) => {
  return isLWOPTOWOPAllDayRequest(timeOffType) ? set_time_to_end_of_day(shiftStart.minus({ days: 1 })) : shiftEnd;
};

export const isWOPPastDeadline = (timeOffType, shiftStart, shiftEnd, station) => {
  const now = DateTime.now().setZone(getTimeZoneFromSwaStation(station));
  const deadline = getWOPDeadline(timeOffType, shiftStart, shiftEnd);
  return now.toMillis() > deadline.toMillis();
};

export const setTimeToMidnightForEADorEDUorSEAD = (startDate, station) => {
  return setStartDateTime(startDate, 0, 0, station);
};

export const isTimeUnknownForShift = (startHour, startMin, endHour, endMin) => {
  return (
    (startHour === 0 && startMin === 0 && endHour === 0 && endMin === 0) ||
    (startHour === "" && startMin === "" && endHour === "" && endMin === "")
  );
};

export const getMinimumShiftLength = (timeOffType) => {
  switch (timeOffType) {
    case HAT_TIME_OFF_TYPE:
    case PTO_TIME_OFF_TYPE:
    case LWOP_ALL_DAY_TIME_OFF_TYPE:
    case TOWOP_ALL_DAY_TIME_OFF_TYPE:
    case TOWOP_DAY_OF_TIME_OFF_TYPE:
    case LWOP_DAY_OF_TIME_OFF_TYPE:
      return MIN_TOR_SHIFT_LENGTH_1_HOUR;
    case DAT_TIME_OFF_TYPE:
    case FTO_TIME_OFF_TYPE:
    case EAD_TIME_OFF_TYPE:
    case EDU_TIME_OFF_TYPE:
    case _4HOUR_VAC_TIME_OFF_TYPE:
    case FTO_GOC_HOL_TIME_OFF_TYPE:
    case SEAD_TIME_OFF_TYPE:
    case HOL_TIME_OFF_TYPE:
      return MIN_TOR_SHIFT_LENGTH_4_HOURS;
    default:
      break;
  }
};
